import { axiosInstance } from '../clients/axiosClient';
import { HEALTH_API_PREFIX, API_V1_PREFIX } from '../utils/constants';
import {
  GetHealthSummariesResponse,
  CreateHealthSummaryRequestPydantic,
  CreateHealthSummaryResponse,
  UpdateHealthSummaryRequest,
  UpdateHealthSummaryResponse,
  GenerateBodySystemSummariesResponsePydantic,
} from '../types/apiGeneratedTypes';

export const getMemberHealthSummaryList = async (
  id: string
): Promise<GetHealthSummariesResponse> => {
  try {
    const { data } = await axiosInstance.get(
      `${API_V1_PREFIX}${HEALTH_API_PREFIX}list?member_id=${id}`
    );
    return data;
  } catch (error: any) {
    throw new Error(error);
  }
};

const generationErrorMessage = (error: any, fallback: string): string => {
  const detail =
    error?.response?.data?.detail ||
    error?.response?.data?.description ||
    error?.response?.data?.message ||
    error?.message ||
    fallback;
  return typeof detail === 'string' ? detail : JSON.stringify(detail);
};

export const generateBodySystemSummaries = async (
  memberId: string
): Promise<GenerateBodySystemSummariesResponsePydantic> => {
  try {
    const { data } = await axiosInstance.post(
      `${API_V1_PREFIX}${HEALTH_API_PREFIX}generate`,
      { member_id: memberId },
      // All body systems × summary + score LLM calls can take several minutes.
      { timeout: 10 * 60 * 1000 }
    );
    return data;
  } catch (error: any) {
    throw new Error(generationErrorMessage(error, 'Failed to generate body system summaries'));
  }
};

export type BulkBodySystemGenerationJob = {
  job_id: string;
  body_system_id: number;
  body_system_title: string;
  status: 'queued' | 'running' | 'completed' | 'cancelled' | 'failed';
  total: number;
  current: number;
  current_member_name: string | null;
  generated: number;
  failed: number;
  failed_names: string[];
  started_at: string;
  finished_at: string | null;
  error: string | null;
};

export const getActiveBulkBodySystemGeneration = async (
  bodySystemId: number
): Promise<BulkBodySystemGenerationJob | null> => {
  try {
    const { data } = await axiosInstance.get(
      `${API_V1_PREFIX}${HEALTH_API_PREFIX}generate/${bodySystemId}/all-members/active`
    );
    return data?.job ?? null;
  } catch (error: any) {
    throw new Error(generationErrorMessage(error, 'Failed to load generation progress'));
  }
};

export const startBulkBodySystemGeneration = async (
  bodySystemId: number
): Promise<BulkBodySystemGenerationJob> => {
  try {
    const { data } = await axiosInstance.post(
      `${API_V1_PREFIX}${HEALTH_API_PREFIX}generate/${bodySystemId}/all-members`,
      {},
      { timeout: 2 * 60 * 1000 }
    );
    return data;
  } catch (error: any) {
    throw new Error(generationErrorMessage(error, 'Failed to start generation'));
  }
};

export const getBulkBodySystemGenerationJob = async (
  jobId: string
): Promise<BulkBodySystemGenerationJob> => {
  try {
    const { data } = await axiosInstance.get(
      `${API_V1_PREFIX}${HEALTH_API_PREFIX}generate/all-members/jobs/${jobId}`
    );
    return data;
  } catch (error: any) {
    throw new Error(generationErrorMessage(error, 'Failed to load generation progress'));
  }
};

export const cancelBulkBodySystemGeneration = async (
  jobId: string
): Promise<BulkBodySystemGenerationJob> => {
  try {
    const { data } = await axiosInstance.post(
      `${API_V1_PREFIX}${HEALTH_API_PREFIX}generate/all-members/jobs/${jobId}/cancel`
    );
    return data;
  } catch (error: any) {
    throw new Error(generationErrorMessage(error, 'Failed to stop generation'));
  }
};

export const generateOneBodySystemSummary = async (
  memberId: string,
  bodySystemId: number
): Promise<GenerateBodySystemSummariesResponsePydantic> => {
  try {
    const { data } = await axiosInstance.post(
      `${API_V1_PREFIX}${HEALTH_API_PREFIX}generate/${bodySystemId}`,
      { member_id: memberId },
      { timeout: 2 * 60 * 1000 }
    );
    return data;
  } catch (error: any) {
    throw new Error(generationErrorMessage(error, 'Failed to generate this body system summary'));
  }
};

export const createHealthSummary = async (
  requestObj: CreateHealthSummaryRequestPydantic
): Promise<CreateHealthSummaryResponse> => {
  try {
    const { data } = await axiosInstance.post(
      `${API_V1_PREFIX}${HEALTH_API_PREFIX}`,
      requestObj
    );

    return data;
  } catch (error: any) {
    throw new Error(error);
  }
};

export const updateHealthSummary = async (
  requestObj: UpdateHealthSummaryRequest
): Promise<UpdateHealthSummaryResponse> => {
  try {
    const { data } = await axiosInstance.patch(
      `${API_V1_PREFIX}${HEALTH_API_PREFIX}`,
      requestObj
    );

    return data;
  } catch (error: any) {
    throw new Error(error);
  }
};
